import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { EditResidentForm, formatPhoneNumber } from './types';

type RoleType = EditResidentForm['role'];

interface EditResidentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resident: EditResidentForm | null;
  onResidentChange: (resident: EditResidentForm | null) => void;
  onSave: () => void;
  getEditablePersonalNumber: (pnr: string | undefined) => string;
}

export function EditResidentDialog({
  open,
  onOpenChange,
  resident,
  onResidentChange,
  onSave,
  getEditablePersonalNumber
}: EditResidentDialogProps) {
  if (!resident) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Redigera boende</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Förnamn *</Label>
              <Input
                value={resident.first_name || ''}
                onChange={(e) => onResidentChange({ ...resident, first_name: e.target.value })}
                data-testid="edit-resident-first-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Efternamn *</Label>
              <Input
                value={resident.last_name || ''}
                onChange={(e) => onResidentChange({ ...resident, last_name: e.target.value })}
                data-testid="edit-resident-last-name-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>E-post *</Label>
            <Input
              type="email"
              value={resident.email || ''}
              onChange={(e) => onResidentChange({ ...resident, email: e.target.value })}
              data-testid="edit-resident-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Telefon</Label>
            <Input
              value={resident.phone || ''}
              onChange={(e) => onResidentChange({ ...resident, phone: formatPhoneNumber(e.target.value) })}
              placeholder="07-123 45 67"
              data-testid="edit-resident-phone-input"
            />
            <p className="text-xs text-slate-400">Format: xx-xxx xx xx</p>
          </div>
          <div className="space-y-2">
            <Label>Personnummer</Label>
            <div className="flex items-center gap-2">
              <Input
                value={getEditablePersonalNumber(resident.personal_number)}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                  onResidentChange({ ...resident, personal_number: digits });
                }}
                placeholder="YYYYMMDD"
                className="font-mono w-32"
                maxLength={8}
                data-testid="edit-resident-pnr-input"
              />
              <span className="text-slate-400 font-mono">-****</span>
            </div>
            <p className="text-xs text-slate-400">De sista 4 siffrorna är krypterade och kan inte visas</p>
          </div>
          <div className="space-y-2">
            <Label>Roll</Label>
            <Select
              value={resident.role || 'resident'}
              onValueChange={(value) => onResidentChange({ ...resident, role: value as RoleType })}
            >
              <SelectTrigger data-testid="edit-resident-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="resident">Boende</SelectItem>
                <SelectItem value="board_member">Styrelsemedlem</SelectItem>
                <SelectItem value="suppliant">Suppleant</SelectItem>
                <SelectItem value="auditor">Revisor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button onClick={onSave} className="flex-1" data-testid="update-resident-button">
              Spara
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
